import CartCommodityItem from '@/classes/CartCommidityItem';
import Product from '@/classes/Product';
import CartInterface from '@/interfaces/CartInterface';
import ProductInterface from '@/interfaces/ProductInterface';
import axios, { AxiosResponse } from 'axios';
import AjaxResponseInterface from '@/interfaces/AjaxResponseInterface';
import ValidationResponseInterface from '@/interfaces/ValidationResponseInterface';
import { getCookie } from '@/helpers';

class OrderAPI {
  public static mapCartResponse(response: AxiosResponse<any>): CartInterface {
    const cartResponseData = response?.data?.data?.cartData;
    const cartData = {
      commodity: [],
      nonCommodity: [],
      campaigns: [],
    } as CartInterface;

    Object.keys(cartResponseData.commodity).forEach((cartItemKey) => {
      const cartItemData = cartResponseData.commodity[cartItemKey];
      cartItemData.product = new Product(cartItemData.product as ProductInterface);

      cartData.commodity.push(new CartCommodityItem(cartItemKey, cartItemData));
    });

    return cartData;
  }

  static validate(step: string, data: unknown): Promise<ValidationResponseInterface> {
    return new Promise<ValidationResponseInterface>((resolve, reject) => {
      let { ajaxUrl } = window.ioappConfig;
      const { protocol } = window.location;

      if (protocol !== 'https:') {
        ajaxUrl = ajaxUrl.replace('https://', 'http://');
      }

      axios.post<ValidationResponseInterface>(`${ajaxUrl}?action=order`, {
        endpoint: 'validate',
        step,
        data,
      }).then((response) => {
        const ajaxResponse = response.data as unknown as AjaxResponseInterface;
        const ajaxResponseData = ajaxResponse.data as ValidationResponseInterface;

        resolve(ajaxResponseData);
      }).catch((e) => {
        reject(e);
      });
    });
  }

  static submit(data: unknown): Promise<ValidationResponseInterface> {
    return new Promise<ValidationResponseInterface>((resolve, reject) => {
      let { ajaxUrl } = window.ioappConfig;
      const { protocol } = window.location;

      if (protocol !== 'https:') {
        ajaxUrl = ajaxUrl.replace('https://', 'http://');
      }

      axios.post<ValidationResponseInterface>(`${ajaxUrl}?action=order`, {
        endpoint: 'submit',
        cartKey: getCookie('io_shop_cart'),
        data,
      }).then((response) => {
        const ajaxResponse = response.data as unknown as AjaxResponseInterface;
        const ajaxResponseData = ajaxResponse.data as ValidationResponseInterface;

        resolve(ajaxResponseData);
      }).catch((e) => {
        reject(e);
      });
    });
  }

  static getFormData(): Promise<Record<string, unknown>> {
    return new Promise<Record<string, unknown>>((resolve, reject) => {
      let { ajaxUrl } = window.ioappConfig;
      const { protocol } = window.location;

      if (protocol !== 'https:') {
        ajaxUrl = ajaxUrl.replace('https://', 'http://');
      }

      axios.post<Record<string, unknown>>(`${ajaxUrl}?action=order`, {
        endpoint: 'getFormData',
      }).then((response) => {
        const ajaxResponse = response.data as unknown as AjaxResponseInterface;
        const ajaxResponseData = ajaxResponse.data as Record<string, unknown>;
        resolve(ajaxResponseData);
      }).catch((e) => {
        reject(e);
      });
    });
  }

  static getLastOrderedItem(): Promise<Record<string, unknown>> {
    return new Promise<Record<string, unknown>>((resolve, reject) => {
      let { ajaxUrl } = window.ioappConfig;
      const { protocol } = window.location;

      if (protocol !== 'https:') {
        ajaxUrl = ajaxUrl.replace('https://', 'http://');
      }

      axios.post<Record<string, unknown>>(`${ajaxUrl}?action=order`, {
        endpoint: 'getLastOrderedItem',
      }).then((response) => {
        const ajaxResponse = response.data as unknown as AjaxResponseInterface;
        const ajaxResponseData = ajaxResponse.data as Record<string, unknown>;
        resolve(ajaxResponseData);
      }).catch((e) => {
        reject(e);
      });
    });
  }

  static deleteLastOrderedItem(): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      let { ajaxUrl } = window.ioappConfig;
      const { protocol } = window.location;

      if (protocol !== 'https:') {
        ajaxUrl = ajaxUrl.replace('https://', 'http://');
      }

      axios.post<Record<string, unknown>>(`${ajaxUrl}?action=order`, {
        endpoint: 'deleteLastOrderedItem',
      }).then(() => {
        resolve(true);
      }).catch((e) => {
        reject(e);
      });
    });
  }

  static activate(hash: string): Promise<number> {
    return new Promise<number>((resolve, reject) => {
      let { ajaxUrl } = window.ioappConfig;
      const { protocol } = window.location;

      if (protocol !== 'https:') {
        ajaxUrl = ajaxUrl.replace('https://', 'http://');
      }

      axios.post<Record<string, unknown>>(`${ajaxUrl}?action=order`, {
        endpoint: 'activate',
        hash,
      }).then((response) => {
        resolve(response.status);
      }).catch((e) => {
        reject(e);
      });
    });
  }

  static storeIncompleteOrder(orderData: Record<any, unknown>, email: string): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      let { ajaxUrl } = window.ioappConfig;
      const { protocol } = window.location;

      if (protocol !== 'https:') {
        ajaxUrl = ajaxUrl.replace('https://', 'http://');
      }

      axios.post<Record<string, unknown>>(`${ajaxUrl}?action=order`, {
        endpoint: 'storeIncomplete',
        cartKey: getCookie('io_shop_cart'),
        orderData,
        email,
      }).then((response) => {
        const ajaxResponse = response.data as unknown as AjaxResponseInterface;
        const ajaxResponseData = ajaxResponse.data as Record<string, unknown>;
        if (
          typeof ajaxResponseData.tokens === 'string'
          && ajaxResponseData.tokens.length > 0
        ) {
          resolve(ajaxResponseData.tokens);
        } else {
          reject(new Error('unable to store order'));
        }
      }).catch((e) => {
        reject(e);
      });
    });
  }

  static validateIncompleteOrderToken(token: string): Promise<{
    cartData: CartInterface,
    cartEnabled: boolean,
    cartKey: string,
  }> {
    return new Promise((resolve, reject) => {
      let { ajaxUrl } = window.ioappConfig;
      const { protocol } = window.location;

      if (protocol !== 'https:') {
        ajaxUrl = ajaxUrl.replace('https://', 'http://');
      }

      axios.post(`${ajaxUrl}?action=order`, {
        endpoint: 'validateIncompleteOrderToken',
        token,
      }).then((response) => {
        resolve({
          cartData: this.mapCartResponse(response),
          cartEnabled: (response?.data?.data?.cartEnabled || false) as boolean,
          cartKey: (response?.data?.data?.cartKey || '') as string,
        });
      }).catch((e) => {
        reject(e);
      });
    });
  }
}

export default OrderAPI;
